import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Segmented, type SegmentedOption } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { HScroller } from "@/shared/components/HScroller";

export type ProductsViewMode = "grid" | "table";

export type ProductsSort =
  | "recent"
  | "sales"
  | "price-desc"
  | "price-asc"
  | "stock-asc"
  | "stock-desc";

export type StatusFilterId = "all" | "active" | "low_stock" | "out_of_stock" | "hidden";

type StatusPill = SegmentedOption<StatusFilterId>;

type Props = {
  basePath: string;
  limit: number;
  selectedCategoryIds: string[];
  status: StatusFilterId;
  search: string;
  statusCounts: Record<StatusFilterId, number>;
  viewMode: ProductsViewMode;
  onViewModeChange: (mode: ProductsViewMode) => void;
  /** Number of active category/sort filters — shown as a badge on the button. */
  activeFilterCount: number;
  /** Opens the shared Filter & sort drawer (category + sort live there). */
  onOpenFilters: () => void;
  /** Extra controls placed after the view toggle (e.g. the bulk-select button). */
  trailing?: ReactNode;
};

const STATUS_DOTS: Record<Exclude<StatusFilterId, "all">, string> = {
  active: "var(--success-500)",
  low_stock: "var(--gold-500)",
  out_of_stock: "var(--error-500)",
  hidden: "#94a3b8",
};

export function ProductsHeroBar({
  basePath,
  limit,
  selectedCategoryIds,
  status,
  search,
  statusCounts,
  viewMode,
  onViewModeChange,
  activeFilterCount,
  onOpenFilters,
  trailing,
}: Props) {
  const t = useTranslations();
  const navigate = useNavigate();

  const buildHref = (overrides: {
    status?: StatusFilterId;
    categoryId?: string | null;
  }) => {
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    params.set("offset", "0");
    const nextStatus = overrides.status ?? status;
    if (nextStatus !== "all") params.set("status", nextStatus);
    const nextCategory =
      overrides.categoryId !== undefined
        ? overrides.categoryId
        : selectedCategoryIds[0] ?? null;
    if (nextCategory) params.append("categoryIds[]", nextCategory);
    if (search) params.set("search", search);
    return `${basePath}?${params.toString()}`;
  };

  const pills: StatusPill[] = [
    {
      value: "all",
      label: t(keys.common.all),
      count: statusCounts.all,
    },
    {
      value: "active",
      label: t(keys.products.statusActive),
      count: statusCounts.active,
      dot: STATUS_DOTS.active,
    },
    {
      value: "low_stock",
      label: t(keys.products.statusLowStock),
      count: statusCounts.low_stock,
      dot: STATUS_DOTS.low_stock,
    },
    {
      value: "out_of_stock",
      label: t(keys.products.statusOutOfStock),
      count: statusCounts.out_of_stock,
      dot: STATUS_DOTS.out_of_stock,
    },
    {
      value: "hidden",
      label: t(keys.products.statusHidden),
      count: statusCounts.hidden,
      dot: STATUS_DOTS.hidden,
    },
  ];

  return (
    // Below lg the toolbar stacks: status tabs on line 1 (scroll instead of
    // wrapping to two lines), then filter + view + select on line 2. At lg+ it
    // collapses onto a single row.
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      {/* Status pill cluster — horizontally scrollable so it never wraps */}
      <HScroller className="no-scrollbar flex overflow-x-auto lg:min-w-0 lg:flex-1">
        <Segmented
          tone="light"
          value={status}
          onChange={(next) => navigate(buildHref({ status: next }))}
          options={pills}
          ariaLabel="Status filter"
        />
      </HScroller>

      <div className="flex shrink-0 items-center gap-2">
        {/* Filter & sort — opens the shared drawer (category + sort live there) */}
        <button
          type="button"
          onClick={onOpenFilters}
          className="inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[12.5px] font-medium text-[color:var(--text-secondary)] transition hover:bg-[color:var(--surface-alt)]"
        >
          <FilterIcon />
          {t(keys.products.filterAndSort)}
          {activeFilterCount > 0 ? (
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[color:var(--accent-500)] px-1 text-[13px] font-semibold leading-none text-[color:var(--on-accent)]">
              {activeFilterCount}
            </span>
          ) : null}
        </button>

        {/* Bulk-select toggle (passed in by the page) */}
        {trailing}

        {/* View toggle */}
        <Segmented
          tone="light"
          value={viewMode}
          onChange={(next) => onViewModeChange(next)}
          options={[
            { value: "grid", label: t(keys.products.viewGrid), icon: <GridIcon /> },
            { value: "table", label: t(keys.products.viewTable), icon: <TableIcon /> },
          ]}
          ariaLabel="View mode"
        />
      </div>
    </div>
  );
}

function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="3" y="4" width="18" height="3" rx="1" />
      <rect x="3" y="10.5" width="18" height="3" rx="1" />
      <rect x="3" y="17" width="18" height="3" rx="1" />
    </svg>
  );
}
